// libraries
import React, { useEffect, useState } from 'react'
import { startCase, toLower } from 'lodash'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { Stack, Divider, Button, Box } from '@mui/material'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { useForm, FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

// state

// queries and mutation
import {
  useGetFreightLanePricesQuery,
  useCreateFreightLaneMutation,
  useUpdateFreightLanePricesMutation,
} from 'services/freightLanes'

// specific components
import FreightLaneBladeInformation from 'components/containers/freight/freight-lanes/FreightLaneBladeInformation'
import FreightLaneBladePricing from 'components/containers/freight/freight-lanes/FreightLaneBladePricing'
import FreightLaneBladePriceHistory from 'components/containers/freight/freight-lanes/FreightLaneBladePriceHistory'

// generic components
import Loading from 'components/generic/Loading'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'

// helpers and utils
import { formatPriceDate } from 'components/containers/freight/utils'
import { getFormOptions } from 'utils/validation/freightLane'
import { dispatchSnackbar } from 'utils/helpers'
import { formatCityState } from 'utils/data-manipulation/valueFormatter'
import { TRANSPORTATION_TYPE_OPTIONS, FREIGHT_LANE_DATE_TYPES } from 'utils/constants'

const FreightLaneBlade = ({ onFormStateChange, onHideModal }) => {
  const { freightLaneId, freightLaneData } = useSelector(state => state.ui.formModalProps)
  const { defaultYardUm, details: yardDetails } = useSelector(state => state.settings)
  const [hasEditedPrices, setHasEditedPrices] = useState(false)

  const isUpdate = !!freightLaneId
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const apiRef = useGridApiRef()

  const { data: freightLanePricesData, isLoading: isLoadingFreightLanePricesData } = useGetFreightLanePricesQuery(
    { id: freightLaneId },
    { skip: !freightLaneId },
  )

  const [createFreightLane, { isLoading: isLoadingCreateFreightLane }] = useCreateFreightLaneMutation()
  const [updateFreightLanePrices, { isLoading: isLoadingUpdateFreightLanePrices }] =
    useUpdateFreightLanePricesMutation()

  const isLoading = isLoadingFreightLanePricesData || isLoadingCreateFreightLane || isLoadingUpdateFreightLanePrices

  const methods = useForm(getFormOptions(isUpdate))
  const { handleSubmit, formState, reset, setError } = methods
  const { dirtyFields } = formState
  const isDirty = !!Object.keys(dirtyFields).length

  useEffect(() => {
    onFormStateChange(isDirty || hasEditedPrices)
  }, [isDirty, hasEditedPrices])

  useEffect(() => {
    if (!freightLaneData || !yardDetails) return
    reset({
      information: {
        carrier: freightLaneData?.carrier?.displayName || '',
        transportationType: freightLaneData?.transportationMode || '',
        fromLocation: formatCityState(yardDetails),
        toLocation: formatCityState(freightLaneData?.location),
      },
    })
  }, [yardDetails, freightLaneData])

  const formatFreightLaneData = data => ({
    carrier: {
      id: data?.information?.carrier?.id,
    },
    location: {
      city: startCase(toLower(data?.information?.city)),
      state: data?.information?.state,
    },
    transportationMode: TRANSPORTATION_TYPE_OPTIONS.find(option => option.id === data?.information?.transportationType)
      ?.name,
  })

  const formatAndValidateFreightLanePrices = data => {
    const dateRanges = []
    const invalidFields = {}
    const formattedFreightLanePrices = []
    Array.from(apiRef.current.getRowModels().values()).forEach(row => {
      const rowFormData = data?.pricing?.[row.id]
      const effectiveDate = formatPriceDate(
        Object.prototype.hasOwnProperty.call(rowFormData || {}, 'effectiveDate')
          ? rowFormData?.effectiveDate || null
          : new Date(row?.effectiveDate),
        FREIGHT_LANE_DATE_TYPES.EFFECTIVE,
      )
      const expirationDate = formatPriceDate(rowFormData?.expirationDate, FREIGHT_LANE_DATE_TYPES.EXPIRATION)

      if (!effectiveDate) {
        invalidFields[`pricing.${row.id}.effectiveDate`] = true
      }
      if (effectiveDate > expirationDate) {
        invalidFields[`pricing.${row.id}.effectiveDate`] = true
        invalidFields[`pricing.${row.id}.expirationDate`] = true
      }

      dateRanges.forEach(range => {
        if (range.effectiveDate > effectiveDate && (!expirationDate || range.effectiveDate < expirationDate)) {
          invalidFields[`pricing.${range.id}.effectiveDate`] = true
          invalidFields[`pricing.${row.id}.expirationDate`] = true
        }
        if (range.expirationDate > effectiveDate && (!expirationDate || range.expirationDate < expirationDate)) {
          invalidFields[`pricing.${range.id}.expirationDate`] = true
          invalidFields[`pricing.${row.id}.effectiveDate`] = true
        }
        if (effectiveDate > range.effectiveDate && (!range.expirationDate || effectiveDate < range.expirationDate)) {
          invalidFields[`pricing.${row.id}.effectiveDate`] = true
          invalidFields[`pricing.${range.id}.expirationDate`] = true
        }
        if (expirationDate > range.effectiveDate && (!range.expirationDate || expirationDate < range.expirationDate)) {
          invalidFields[`pricing.${row.id}.expirationDate`] = true
          invalidFields[`pricing.${range.id}.effectiveDate`] = true
        }
        if (range.effectiveDate === effectiveDate) {
          invalidFields[`pricing.${range.id}.effectiveDate`] = true
          invalidFields[`pricing.${row.id}.effectiveDate`] = true
        }
        if (range.expirationDate === expirationDate) {
          invalidFields[`pricing.${range.id}.expirationDate`] = true
          invalidFields[`pricing.${row.id}.expirationDate`] = true
        }
      })

      dateRanges.push({
        id: row.id,
        effectiveDate,
        expirationDate,
      })
      formattedFreightLanePrices.push({
        id: row.id,
        effectiveDate,
        expirationDate,
        um: (row.um || defaultYardUm).toLowerCase(),
        costPerUm: Number(row.costPerUm || 0),
        minCost: Number(row.minCost || 0),
        fuelSurcharge: Number(row.fuelSurcharge || 0),
      })
    })
    return {
      formattedFreightLanePrices,
      invalidFieldNames: Object.keys(invalidFields),
    }
  }

  const onSubmit = async data => {
    try {
      const formattedFreightLaneData = formatFreightLaneData(data)
      const { formattedFreightLanePrices, invalidFieldNames } = formatAndValidateFreightLanePrices(data)

      if (formattedFreightLanePrices.length === 0) {
        dispatchSnackbar(dispatch, 'error', 'At least one active/upcoming price is required')
        return
      }

      if (invalidFieldNames.length > 0) {
        invalidFieldNames.forEach(fieldName => {
          setError(fieldName, {
            type: 'custom',
            message: 'Invalid Date',
          })
        })
        dispatchSnackbar(dispatch, 'error', 'Some date ranges are invalid or overlapping')
        return
      }

      let freightLaneIdToUpdate = freightLaneId

      if (!isUpdate) {
        const { data: createFreightLaneData, error: createFreightLaneError } = await createFreightLane({
          body: formattedFreightLaneData,
        })
        if (createFreightLaneError) throw new Error(createFreightLaneError?.data?.error)

        freightLaneIdToUpdate = createFreightLaneData.id
      }

      const { error: updateFreightLanePricesError } = await updateFreightLanePrices({
        id: freightLaneIdToUpdate,
        body: formattedFreightLanePrices.map(price => ({
          ...price,
          freightLaneId: freightLaneIdToUpdate,
        })),
      })
      if (updateFreightLanePricesError) throw new Error(updateFreightLanePricesError?.data?.error)

      dispatchSnackbar(dispatch, 'success', `Freight lane successfully ${isUpdate ? 'updated' : 'created'}`)
      onHideModal({ skipBlocker: true })
    } catch (error) {
      const message =
        error?.data?.error ||
        error?.message ||
        `An error occurred while ${isUpdate ? 'updating' : 'creating'} the freight lane`
      dispatchSnackbar(dispatch, 'error', message)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isLoading && <Loading />}
      <Stack height="100vh">
        <FormModalHeading
          title={isUpdate ? `${formatCityState(freightLaneData?.location)}` : 'New Freight Lane'}
          backButtonPosition="right"
          onBack={() => onHideModal(navigate)}
        />
        <Divider />
        <Box px={4} py={2} flexGrow={1}>
          <FormProvider {...methods}>
            <FreightLaneBladeInformation isUpdate={isUpdate} />
            <FreightLaneBladePricing
              freightLanePricesData={freightLanePricesData}
              apiRef={apiRef}
              isUpdate={isUpdate}
              setHasEditedPrices={setHasEditedPrices}
            />
            {isUpdate && <FreightLaneBladePriceHistory freightLanePricesData={freightLanePricesData} />}
          </FormProvider>
        </Box>
        <Divider />
        <Stack direction="row" p={2} spacing={2}>
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={!isDirty && !hasEditedPrices}>
            Save
          </Button>
          <Button variant="outlined" onClick={() => onHideModal()}>
            Cancel
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}

FreightLaneBlade.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

FreightLaneBlade.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default FreightLaneBlade
